import type { AppListing } from '@/backend/types';

/**
 * The apps a new person sees before they have built anything.
 *
 * NOT listed in My Apps any more. My Apps is the signed-in person's own finished, live apps
 * (`src/utils/myApps.ts`), and a demo is nobody's project there — the owner asked for exactly
 * that. What still reads this list: the intent screen's "See a real one first" link
 * (`showcaseDemoUrl`), the welcome carousel's count, and `myAppsStore`, which the app-detail and
 * invite screens use to tell whether an app is already joined.
 *
 * These are not mock-ups and not a separate code path: each one was generated by Saddle
 * from a sentence, gated and published like anyone else's, and each is live at the URL
 * below. Opening one opens exactly what a person's own app becomes — which is the whole
 * claim the product makes, so demonstrating it with anything else would be a lie told at
 * the first moment it could be told.
 *
 * They replace two demos built for the previous product: apps the on-device engine ran
 * from a JSON definition, one of them wired to HealthKit. Both still worked, and both
 * showed a Saddle that no longer exists.
 *
 * The copy here is written rather than taken from the catalog: an app's stored description
 * is the prompt it was built from, truncated mid-word, which is fine for a row in a list
 * of your own apps and not fine as the first sentence anyone reads about Saddle.
 *
 * Rebuilding the set: `E2E_DEMOS=1 npx jest demos.live` publishes them and writes
 * /tmp/saddle-demos/demos.json with the URLs. If a slug changes, change it here too.
 */

const CREATOR = 'saddle-demos';
const PUBLISHED_AT = '2026-09-09T00:00:00.000Z';

interface WebDemo {
  /**
   * The app's REAL id in the catalog — deliberately not a `demo:` id.
   *
   * That prefix is reserved for apps the on-device engine runs out of local SQLite, and
   * anything wearing it gets routed there. These are published web apps: a real id means
   * every path that looks one up gets a real answer instead of a spinner.
   */
  appId: string;
  slug: string;
  name: string;
  /** One sentence, in the voice of the person who would have made it. */
  description: string;
  icon: string;
  category: string;
  kit: string;
  /** What this one is here to show, for the card's second line. */
  proves: string;
  /**
   * The runtime call `proves` depends on — the mechanism, named, so the claim is checkable.
   *
   * Two demos shipped claiming things their code could not do: "accounts, an answer that
   * follows you between devices" on an app with no `S.auth` and no `S.cloud`, and "a double
   * opt-in list that actually mails" on one with no `S.subscribe`. Both rendered perfectly.
   * Prose cannot be verified; a call name can, and `scripts/verify-demo-claims.sh` reads
   * THIS field rather than keeping its own list — a checker with its own copy of the truth
   * reports on its copy.
   *
   * ⚠️ IT IS WEAKER THAN IT LOOKS. This check was GREEN through the whole spinner outage:
   * `S.cloud` was present the entire time the app hung for every signed-out visitor,
   * because a call being there says the claim is not fabricated and says nothing about
   * whether the app handles the state it puts a visitor in. Pair it with the loading check
   * in `verify-published-app.sh`, and never read a green `provesCall` as "the demo works".
   */
  provesCall: string;
}

const DEMOS: WebDemo[] = [
  {
    appId: 'bmlqDWbr6aLUM1WFRPQ-D',
    slug: 'pottery-class-bookings-2',
    name: 'Pottery classes',
    description: 'A timetable of classes people book a place on. A full session says so instead of taking another booking.',
    icon: 'color-palette',
    category: 'Bookings',
    kit: 'ledger',
    proves: 'Places held by the server, not by the page',
    provesCall: 'S.seats',
  },
  {
    appId: 'mg3piEmbXoA2wgPbqVhYx',
    slug: 'cafe-rota',
    name: 'Cafe rota',
    description: "The week's shifts, posted by the manager and claimed by staff. Tuesday can go up on Monday night.",
    icon: 'cafe',
    category: 'Work',
    kit: 'console',
    proves: 'Content the owner changes without rebuilding',
    provesCall: 'S.list',
  },
  {
    appId: 'mbOKm01Hmjcxm5DG5ny1W',
    slug: 'food-bank-shift-board-4',
    name: 'Volunteer shifts',
    description: 'A shift board for a food bank. Volunteers claim a slot; a full shift stops taking people and past ones grey out.',
    icon: 'people',
    category: 'Community',
    kit: 'ledger',
    proves: 'A limit that refuses the next claim',
    provesCall: 'S.seats',
  },
  {
    /*
     * REPLACED 2026-09-23. The previous app at `small-batch-coffee-2` carried the line
     * "a double opt-in list that actually mails" and contained NO `S.subscribe` — it
     * collected addresses with `S.submit`, which sends nothing and leaves the owner a list
     * they cannot lawfully mail. The claim was printed in the app and on the public site.
     *
     * This one is verified: POST /subscribe answers {status:'pending', emailed:true}, so
     * the address is pending until the person clicks and an email genuinely went out.
     */
    appId: 'OuUqrqei_uZ67IUaV9piC',
    slug: 'coffee-launch-list',
    name: 'Coffee waiting list',
    description: 'A launch page with a real waiting list. You confirm by email, and the page invents no numbers.',
    icon: 'mail',
    category: 'Launch',
    kit: 'soft',
    proves: 'A double opt-in list that actually mails',
    provesCall: 'S.subscribe',
  },
  {
    /*
     * REPLACED 2026-09-23. The previous app at `our-wedding-weekend-3` claimed "accounts —
     * an answer that follows you between devices" and had no `S.auth` and no `S.cloud` at
     * all: a single `S.submit('rsvps')` and nothing else. There was no sign-in, so nothing
     * could follow anybody anywhere, and a guest could not change an answer they had given.
     *
     * This one is verified end to end: two signed-in guests each saved an RSVP, and each
     * read back ONLY their own.
     */
    appId: 'uKF4DHnHw9QXE0TJYhEOo',
    slug: 'wedding-weekend-accounts',
    name: 'Wedding weekend',
    description: 'Guests sign in, say what they are coming to, and change their answer later from any device.',
    icon: 'heart',
    category: 'Events',
    kit: 'editorial',
    proves: 'Accounts — an answer that follows you between devices',
    provesCall: 'S.cloud',
  },
  {
    appId: 'hX98MEiGaQEa1Q31L6tmH',
    slug: 'ammiya-2',
    name: 'Everyday Arabic',
    description: 'A deck that writes its own cards, reviews you on them, and brings back whatever you keep missing.',
    icon: 'school',
    category: 'Learning',
    kit: 'sport',
    proves: 'AI making the content, and right-to-left text',
    provesCall: 'S.ai',
  },
];

/** The demo to show somebody who has not signed up yet — a real published page, opened in
 *  the browser, so "see it first" does not require an account. Narrow on purpose: the whole
 *  DEMOS array is not exported, because callers should not be picking one by index. */
export function showcaseDemoUrl(): string {
  return demoSiteUrl(DEMOS[0].slug);
}

/** Where a bundled demo is published. Absolute, because it is opened in a browser. */
export function demoSiteUrl(slug: string): string {
  return `https://app.saddle-sites.com/site/${slug}`;
}

/**
 * A demo as a listing.
 *
 * `webPublished` is what tells every surface to open the URL rather than hand the app to
 * the on-device engine, which has no idea what these are. `remixOnly` is what puts the
 * DEMO badge on the card.
 */
export function webDemoListings(): AppListing[] {
  return DEMOS.map((d) => ({
    appId: d.appId,
    name: d.name,
    description: d.description,
    icon: d.icon,
    category: d.category,
    creatorId: CREATOR,
    publishedAt: PUBLISHED_AT,
    remixOnly: true,
    webPublished: true,
    webVisibility: 'public' as const,
    slug: d.slug,
    kit: d.kit,
    siteUrl: demoSiteUrl(d.slug),
  }));
}

/** What a demo is in the set to show, for a card that has room to say. */
export function demoProves(appId: string): string | undefined {
  return DEMOS.find((d) => d.appId === appId)?.proves;
}

/** True for a bundled demo, so a surface can badge it without hardcoding ids. */
export function isWebDemo(appId: string | undefined | null): boolean {
  return !!appId && DEMOS.some((d) => d.appId === appId);
}

/*
 * The server's copy of this list is fetched in ./fetchWebDemos.ts, not here: this module is a
 * constant list and lookups with no IO, which is what lets the web builder import it.
 */
